import { useCallback, useEffect, useRef, useState } from "react";
import {
  usePrivy,
  useWallets,
  useLoginWithTelegram,
  useCreateWallet,
  useSignMessage as useEvmSignMessage,
  useSendTransaction as useEvmSendTransaction,
} from "@privy-io/react-auth";
import {
  useSolanaWallets,
  useSignMessage as useSolSignMessage,
  useSendTransaction as useSolSendTransaction,
} from "@privy-io/react-auth/solana";
import {
  Connection,
  PublicKey,
  SystemProgram,
  Transaction,
  LAMPORTS_PER_SOL,
} from "@solana/web3.js";
import { createPublicClient, formatEther, http, parseEther, type Hex } from "viem";
import WebApp from "@twa-dev/sdk";
import {
  BASE_SEPOLIA_FAUCET,
  EVM_CHAIN,
  EVM_RPC,
  SOLANA_HTTP,
  SOL_FAUCET,
  basescanTx,
  solscanTx,
} from "./config";

type LinkResult = {
  ok: boolean;
  privyDid?: string;
  telegramUserId?: string;
  wallets?: { chainType: string; address: string }[];
  error?: string;
};

async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

function toHex(bytes: Uint8Array): string {
  return `0x${[...bytes].map((b) => b.toString(16).padStart(2, "0")).join("")}`;
}

function haptic(kind: "success" | "error" | "warning" = "success") {
  try {
    WebApp.HapticFeedback.notificationOccurred(kind);
  } catch {
    /* ignore */
  }
}

function CopyableAddress({
  label,
  value,
  loading,
  balance,
}: {
  label: string;
  value?: string | null;
  loading?: boolean;
  balance?: string | null;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  const onCopy = async () => {
    if (!value) return;
    const ok = await copyText(value);
    if (!ok) return;
    setCopied(true);
    haptic("success");
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="addr-block">
      <div className="addr-head">
        <span className="label">{label}</span>
        <div className="addr-head-right">
          {balance != null && <span className="bal-pill">{balance}</span>}
          {value && (
            <button
              type="button"
              className={`copy-chip ${copied ? "ok" : ""}`}
              onClick={() => void onCopy()}
            >
              {copied ? "Copied" : "Copy"}
            </button>
          )}
        </div>
      </div>
      {loading && !value ? (
        <span className="muted">Creating…</span>
      ) : value ? (
        <button
          type="button"
          className={`addr-full mono ${copied ? "copied" : ""}`}
          onClick={() => void onCopy()}
          title="Tap to copy"
        >
          {value}
        </button>
      ) : (
        <span className="muted">—</span>
      )}
    </div>
  );
}

export default function App() {
  const { ready, authenticated, user, logout, getAccessToken } = usePrivy();
  const { login: loginTelegram, state: tgLoginState } = useLoginWithTelegram();
  const { createWallet: createEvmWallet } = useCreateWallet();
  const { wallets: evmWallets } = useWallets();
  const { wallets: solWallets, createWallet: createSolWallet } = useSolanaWallets();

  const { signMessage: signEvmMessage } = useEvmSignMessage();
  const { sendTransaction: sendEvmTx } = useEvmSendTransaction();
  const { signMessage: signSolMessage } = useSolSignMessage();
  const { sendTransaction: sendSolTx } = useSolSendTransaction();

  const solConnection = useMemoConn();

  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [walletBusy, setWalletBusy] = useState(false);
  const [linkState, setLinkState] = useState<"idle" | "linking" | "linked" | "error">(
    "idle"
  );
  const [linkError, setLinkError] = useState<string | null>(null);
  const [linkPayload, setLinkPayload] = useState<LinkResult | null>(null);

  const [solBal, setSolBal] = useState<string | null>(null);
  const [evmBal, setEvmBal] = useState<string | null>(null);
  const [balLoading, setBalLoading] = useState(false);

  const [signMsg, setSignMsg] = useState("Hello from BONKd devnet");
  const [signChain, setSignChain] = useState<"sol" | "evm">("sol");
  const [signBusy, setSignBusy] = useState(false);
  const [signResult, setSignResult] = useState<string | null>(null);
  const [signError, setSignError] = useState<string | null>(null);

  const [sendChain, setSendChain] = useState<"sol" | "evm">("sol");
  const [sendTo, setSendTo] = useState("");
  const [sendAmt, setSendAmt] = useState("0.01");
  const [sendBusy, setSendBusy] = useState(false);
  const [sendResult, setSendResult] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);

  const autoLoginTried = useRef(false);
  const walletCreateTried = useRef(false);

  const tgUser = (() => {
    try {
      return WebApp.initDataUnsafe?.user ?? null;
    } catch {
      return null;
    }
  })();

  const inTelegram = Boolean(
    typeof window !== "undefined" &&
      (window as unknown as { Telegram?: { WebApp?: { initData?: string } } })
        .Telegram?.WebApp?.initData
  );

  useEffect(() => {
    try {
      WebApp.ready();
      WebApp.expand();
      WebApp.setHeaderColor("#0b0f14");
      WebApp.setBackgroundColor("#0b0f14");
    } catch {
      /* browser preview */
    }
  }, []);

  type LinkedLike = {
    type?: string;
    address?: string;
    chainType?: string;
  };

  const linked = (user?.linkedAccounts || []) as LinkedLike[];

  const solAddress =
    solWallets?.[0]?.address ??
    linked.find((a) => a.type === "wallet" && a.chainType === "solana")?.address;

  const evmAddress =
    evmWallets?.[0]?.address ??
    linked.find(
      (a) =>
        a.type === "wallet" &&
        (a.chainType === "ethereum" || a.chainType === undefined)
    )?.address;

  const refreshBalances = useCallback(async () => {
    if (!solAddress && !evmAddress) return;
    setBalLoading(true);
    try {
      if (solAddress) {
        try {
          const lamports = await solConnection.getBalance(new PublicKey(solAddress));
          setSolBal(`${(lamports / LAMPORTS_PER_SOL).toFixed(4)} SOL`);
        } catch {
          setSolBal("—");
        }
      }
      if (evmAddress) {
        try {
          const client = createPublicClient({
            chain: EVM_CHAIN,
            transport: http(EVM_RPC),
          });
          const wei = await client.getBalance({ address: evmAddress as Hex });
          setEvmBal(`${Number(formatEther(wei)).toFixed(6)} ETH`);
        } catch {
          setEvmBal("—");
        }
      }
    } finally {
      setBalLoading(false);
    }
  }, [solAddress, evmAddress, solConnection]);

  useEffect(() => {
    if (!authenticated) return;
    void refreshBalances();
    const t = setInterval(() => void refreshBalances(), 20_000);
    return () => clearInterval(t);
  }, [authenticated, refreshBalances]);

  const doLogin = useCallback(async () => {
    if (!ready || authenticated || authBusy) return;
    setAuthBusy(true);
    setAuthError(null);
    try {
      await loginTelegram();
    } catch (e) {
      setAuthError(e instanceof Error ? e.message : String(e));
      haptic("error");
    } finally {
      setAuthBusy(false);
    }
  }, [ready, authenticated, authBusy, loginTelegram]);

  useEffect(() => {
    if (!ready || !inTelegram || authenticated || autoLoginTried.current) return;
    autoLoginTried.current = true;
    void doLogin();
  }, [ready, inTelegram, authenticated, doLogin]);

  useEffect(() => {
    if (!ready || !authenticated || walletCreateTried.current) return;
    const needSol = !solAddress;
    const needEvm = !evmAddress;
    if (!needSol && !needEvm) return;

    walletCreateTried.current = true;
    setWalletBusy(true);
    void (async () => {
      try {
        if (needEvm) await createEvmWallet().catch(() => undefined);
        if (needSol && createSolWallet) await createSolWallet().catch(() => undefined);
      } finally {
        setWalletBusy(false);
      }
    })();
  }, [
    ready,
    authenticated,
    solAddress,
    evmAddress,
    createEvmWallet,
    createSolWallet,
  ]);

  const linkToBot = useCallback(async () => {
    const apiBase = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(
      /\/$/,
      ""
    );
    if (!apiBase) {
      setLinkState("error");
      setLinkError("VITE_API_BASE is not set");
      return;
    }
    if (!authenticated) {
      setLinkState("error");
      setLinkError("Not authenticated");
      return;
    }

    setLinkState("linking");
    setLinkError(null);

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) throw new Error("No Privy access token");

      const initData =
        WebApp.initData ||
        (window as unknown as { Telegram?: { WebApp?: { initData?: string } } })
          .Telegram?.WebApp?.initData ||
        "";

      if (!initData) {
        throw new Error(
          "No Telegram initData — open this page as a Mini App inside Telegram"
        );
      }

      const res = await fetch(`${apiBase}/api/link`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ accessToken, initData }),
      });

      const data = (await res.json()) as LinkResult;
      if (!res.ok || !data.ok) {
        throw new Error(data.error || `Link failed (${res.status})`);
      }

      setLinkPayload(data);
      setLinkState("linked");
      haptic("success");
    } catch (e) {
      setLinkState("error");
      setLinkError(e instanceof Error ? e.message : String(e));
      haptic("error");
    }
  }, [authenticated, getAccessToken]);

  useEffect(() => {
    if (!ready || !authenticated || !inTelegram) return;
    if (walletBusy) return;
    if (linkState === "linking" || linkState === "linked") return;
    void linkToBot();
  }, [ready, authenticated, inTelegram, walletBusy, linkState, linkToBot]);

  const onSign = async () => {
    setSignBusy(true);
    setSignError(null);
    setSignResult(null);
    try {
      if (signChain === "sol") {
        if (!solAddress) throw new Error("No Solana wallet");
        const bytes = new TextEncoder().encode(signMsg);
        const sig = await signSolMessage({
          message: bytes,
          options: { address: solAddress },
        });
        setSignResult(toHex(sig));
      } else {
        if (!evmAddress) throw new Error("No EVM wallet");
        // ensure chain
        const w = evmWallets.find(
          (x) => x.address.toLowerCase() === evmAddress.toLowerCase()
        );
        if (w?.switchChain) {
          await w.switchChain(EVM_CHAIN.id).catch(() => undefined);
        }
        const { signature } = await signEvmMessage(
          { message: signMsg },
          { address: evmAddress }
        );
        setSignResult(signature);
      }
      haptic("success");
    } catch (e) {
      setSignError(e instanceof Error ? e.message : String(e));
      haptic("error");
    } finally {
      setSignBusy(false);
    }
  };

  const onSend = async () => {
    setSendBusy(true);
    setSendError(null);
    setSendResult(null);
    try {
      const amt = Number(sendAmt);
      if (!Number.isFinite(amt) || amt <= 0) throw new Error("Invalid amount");
      if (!sendTo.trim()) throw new Error("Recipient required");

      if (sendChain === "sol") {
        if (!solAddress) throw new Error("No Solana wallet");
        let toPk: PublicKey;
        try {
          toPk = new PublicKey(sendTo.trim());
        } catch {
          throw new Error("Invalid Solana address");
        }
        const fromPk = new PublicKey(solAddress);
        const { blockhash, lastValidBlockHeight } =
          await solConnection.getLatestBlockhash("confirmed");
        const tx = new Transaction({
          feePayer: fromPk,
          blockhash,
          lastValidBlockHeight,
        }).add(
          SystemProgram.transfer({
            fromPubkey: fromPk,
            toPubkey: toPk,
            lamports: Math.round(amt * LAMPORTS_PER_SOL),
          })
        );
        const receipt = await sendSolTx({
          transaction: tx,
          connection: solConnection,
          address: solAddress,
        });
        const sig =
          (receipt as { signature?: string }).signature ||
          (receipt as { txid?: string }).txid ||
          String(receipt);
        setSendResult(sig);
        void refreshBalances();
      } else {
        if (!evmAddress) throw new Error("No EVM wallet");
        if (!/^0x[a-fA-F0-9]{40}$/.test(sendTo.trim())) {
          throw new Error("Invalid EVM address");
        }
        const w = evmWallets.find(
          (x) => x.address.toLowerCase() === evmAddress.toLowerCase()
        );
        if (w?.switchChain) {
          await w.switchChain(EVM_CHAIN.id).catch(() => undefined);
        }
        const { hash } = await sendEvmTx(
          {
            to: sendTo.trim() as Hex,
            value: parseEther(String(amt)),
            chainId: EVM_CHAIN.id,
          },
          { address: evmAddress }
        );
        setSendResult(hash);
        void refreshBalances();
      }
      haptic("success");
    } catch (e) {
      setSendError(e instanceof Error ? e.message : String(e));
      haptic("error");
    } finally {
      setSendBusy(false);
    }
  };

  const tgStatus =
    typeof tgLoginState === "object" && tgLoginState && "status" in tgLoginState
      ? String((tgLoginState as { status?: string }).status)
      : "";

  if (!ready) {
    return (
      <div className="screen center">
        <div className="spinner" />
        <p className="muted">Starting…</p>
      </div>
    );
  }

  return (
    <div className="screen">
      <header className="header">
        <div className="badge-row">
          <div className="badge">BONK'd</div>
          <div className="badge badge-dev">DEVNET</div>
        </div>
        <h1>Wallet</h1>
        <p className="muted">
          Solana Devnet · Base Sepolia
          {tgUser?.username
            ? ` · @${tgUser.username}`
            : tgUser?.id
              ? ` · TG ${tgUser.id}`
              : ""}
        </p>
      </header>

      {!inTelegram && (
        <div className="card warn">
          <strong>Open inside Telegram</strong>
          <p className="muted">
            Use the bot <code>/start</code> → Open wallet.
          </p>
        </div>
      )}

      {!authenticated ? (
        <div className="card">
          <p className="muted">
            {authBusy || tgStatus === "loading" || tgStatus === "submitting"
              ? "Signing you in…"
              : "Connect with Telegram. No seed phrases."}
          </p>
          {(authBusy || tgStatus === "loading" || tgStatus === "submitting") && (
            <div className="center-row">
              <div className="spinner" />
            </div>
          )}
          {authError && <p className="error">{authError}</p>}
          <button
            className="btn primary"
            type="button"
            disabled={authBusy}
            onClick={() => {
              autoLoginTried.current = true;
              void doLogin();
            }}
          >
            {authBusy ? "Connecting…" : "Continue with Telegram"}
          </button>
        </div>
      ) : (
        <>
          <div className="card">
            <div className="row">
              <span className="label">Status</span>
              <span className="value ok">Connected · Devnet</span>
            </div>
            <div className="row">
              <span className="label">Balances</span>
              <button
                type="button"
                className="copy-chip"
                onClick={() => void refreshBalances()}
                disabled={balLoading}
              >
                {balLoading ? "…" : "Refresh"}
              </button>
            </div>
            <CopyableAddress
              label="Solana"
              value={solAddress}
              loading={walletBusy}
              balance={solBal}
            />
            <CopyableAddress
              label="EVM (Base Sepolia)"
              value={evmAddress}
              loading={walletBusy}
              balance={evmBal}
            />
            <div className="faucet-row">
              <a className="link-btn" href={SOL_FAUCET} target="_blank" rel="noreferrer">
                SOL faucet
              </a>
              <a
                className="link-btn"
                href={BASE_SEPOLIA_FAUCET}
                target="_blank"
                rel="noreferrer"
              >
                ETH faucet
              </a>
            </div>
          </div>

          <div className="card">
            <div className="card-title">Sign message</div>
            <div className="seg">
              <button
                type="button"
                className={`seg-btn ${signChain === "sol" ? "on" : ""}`}
                onClick={() => setSignChain("sol")}
              >
                Solana
              </button>
              <button
                type="button"
                className={`seg-btn ${signChain === "evm" ? "on" : ""}`}
                onClick={() => setSignChain("evm")}
              >
                EVM
              </button>
            </div>
            <textarea
              className="input area"
              value={signMsg}
              onChange={(e) => setSignMsg(e.target.value)}
              rows={3}
              placeholder="Message to sign"
            />
            <button
              className="btn primary"
              type="button"
              disabled={signBusy || !signMsg.trim()}
              onClick={() => void onSign()}
            >
              {signBusy ? "Signing…" : "Sign"}
            </button>
            {signError && <p className="error">{signError}</p>}
            {signResult && (
              <div className="result-box">
                <div className="addr-head">
                  <span className="label">Signature</span>
                  <button
                    type="button"
                    className="copy-chip"
                    onClick={() => void copyText(signResult)}
                  >
                    Copy
                  </button>
                </div>
                <code className="mono break">{signResult}</code>
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-title">Send</div>
            <div className="seg">
              <button
                type="button"
                className={`seg-btn ${sendChain === "sol" ? "on" : ""}`}
                onClick={() => setSendChain("sol")}
              >
                SOL
              </button>
              <button
                type="button"
                className={`seg-btn ${sendChain === "evm" ? "on" : ""}`}
                onClick={() => setSendChain("evm")}
              >
                ETH
              </button>
            </div>
            <label className="field-label">To</label>
            <input
              className="input"
              value={sendTo}
              onChange={(e) => setSendTo(e.target.value)}
              placeholder={
                sendChain === "sol" ? "Solana address" : "0x… Base Sepolia"
              }
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
            />
            <label className="field-label">Amount</label>
            <input
              className="input"
              value={sendAmt}
              onChange={(e) => setSendAmt(e.target.value)}
              inputMode="decimal"
              placeholder={sendChain === "sol" ? "0.01 SOL" : "0.001 ETH"}
            />
            <button
              className="btn primary"
              type="button"
              disabled={sendBusy}
              onClick={() => void onSend()}
            >
              {sendBusy
                ? "Sending…"
                : `Send ${sendChain === "sol" ? "SOL" : "ETH"}`}
            </button>
            {sendError && <p className="error">{sendError}</p>}
            {sendResult && (
              <div className="result-box">
                <div className="addr-head">
                  <span className="label">Tx</span>
                  <a
                    className="copy-chip ok"
                    href={
                      sendChain === "sol"
                        ? solscanTx(sendResult)
                        : basescanTx(sendResult)
                    }
                    target="_blank"
                    rel="noreferrer"
                  >
                    Explorer
                  </a>
                </div>
                <code className="mono break">{sendResult}</code>
              </div>
            )}
          </div>

          <div className="card">
            <div className="row">
              <span className="label">Bot</span>
              <span className="value">
                {linkState === "linked" && "Linked"}
                {linkState === "linking" && "Linking…"}
                {linkState === "idle" && "Pending"}
                {linkState === "error" && "Failed"}
              </span>
            </div>
            {linkError && <p className="error">{linkError}</p>}
            {linkPayload?.wallets && linkPayload.wallets.length > 0 && (
              <div className="linked-wallets">
                {linkPayload.wallets.map((w) => (
                  <CopyableAddress
                    key={`${w.chainType}-${w.address}`}
                    label={w.chainType}
                    value={w.address}
                  />
                ))}
              </div>
            )}
            <div className="actions">
              <button
                className="btn primary"
                type="button"
                disabled={linkState === "linking" || walletBusy}
                onClick={() => void linkToBot()}
              >
                {linkState === "linked" ? "Refresh link" : "Link to bot"}
              </button>
              <button
                className="btn ghost"
                type="button"
                onClick={() => {
                  setLinkState("idle");
                  setLinkPayload(null);
                  walletCreateTried.current = false;
                  autoLoginTried.current = false;
                  void logout();
                }}
              >
                Log out
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function useMemoConn() {
  // stable Connection for Solana devnet
  const ref = useRef<Connection | null>(null);
  if (!ref.current) {
    ref.current = new Connection(SOLANA_HTTP, "confirmed");
  }
  return ref.current;
}
